import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Animated,
  Image,
  Platform,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
  type ImageSourcePropType,
} from 'react-native'
import {
  CalendarCheck,
  ChartNoAxesCombined,
  Users,
  type LucideIcon,
} from 'lucide-react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { LanguageContext } from '../../../src/i18n'
import { useTheme } from '../../../src/theme/useTheme'
import { ASSET_PATHS, getCdnUrlExactKey } from '../../../constants/assets'
import {
  getLandingFeaturesStyles,
  landingFeatureVisualGlowEnd,
  landingFeatureVisualGlowStart,
} from './LandingFeatures.styles'
import { LANDING_PHONE_MAX_WIDTH } from '../../../src/landing/constants/landingMarketingBreakpoints.constants'

interface LandingFeature {
  id: number
  Icon: LucideIcon
  title: string
  description: string
  image: ImageSourcePropType
  accessibilityLabel: string
}

const FEATURE_ROTATION_MS = 10000
const STACKED_FEATURE_WIDTH = 920
const FEATURE_CARD_GAP = 14
const PHONE_FEATURE_WIDTH = LANDING_PHONE_MAX_WIDTH

export function LandingFeatures() {
  const theme = useTheme()
  const styles = useMemo(() => getLandingFeaturesStyles(theme), [theme])
  const languageContext = useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('LandingFeatures must be used within LanguageProvider')
  }
  const { t } = languageContext
  const features = useMemo<LandingFeature[]>(
    () => [
      {
        id: 1,
        Icon: Users,
        title: t.features.avatars.title,
        description: t.features.avatars.description,
        image: { uri: getCdnUrlExactKey(ASSET_PATHS.LANDING_FEATURE_1) },
        accessibilityLabel: t.features.avatars.accessibilityLabel,
      },
      {
        id: 2,
        Icon: CalendarCheck,
        title: t.features.locks.title,
        description: t.features.locks.description,
        image: { uri: getCdnUrlExactKey(ASSET_PATHS.LANDING_APP_FIXTURES) },
        accessibilityLabel: t.features.locks.title,
      },
      {
        id: 3,
        Icon: ChartNoAxesCombined,
        title: t.features.track.title,
        description: t.features.track.description,
        image: { uri: getCdnUrlExactKey(ASSET_PATHS.LANDING_FEATURE_3) },
        accessibilityLabel: t.features.track.title,
      },
    ],
    [t],
  )

  const [currentFeature, setCurrentFeature] = useState(0)
  const [restartSignal, setRestartSignal] = useState(0)
  const progressAnim = useRef(new Animated.Value(0)).current
  const imageAnim = useRef(new Animated.Value(1)).current
  const featureRailRef = useRef<ScrollView | null>(null)
  const { width } = useWindowDimensions()
  const isStacked = width > 0 && width < STACKED_FEATURE_WIDTH
  const isPhone = width > 0 && width < PHONE_FEATURE_WIDTH
  const activeFeature = features[currentFeature]

  const phoneFeatureCardWidth = useMemo(() => {
    if (!isPhone || width <= 0) return null
    return Math.min(Math.max(width - 48, 248), 302)
  }, [isPhone, width])

  const phoneFeatureCardWidthStyle = useMemo(
    () => phoneFeatureCardWidth != null ? { width: phoneFeatureCardWidth } : null,
    [phoneFeatureCardWidth],
  )

  const featureRailScrollStep = useMemo(() => {
    if (!isStacked) return 288
    const cardW = phoneFeatureCardWidth ?? 274
    return cardW + FEATURE_CARD_GAP
  }, [isStacked, phoneFeatureCardWidth])

  useEffect(() => {
    progressAnim.stopAnimation()
    progressAnim.setValue(0)

    const animation = Animated.timing(progressAnim, {
      toValue: 1,
      duration: FEATURE_ROTATION_MS,
      useNativeDriver: false,
    })

    animation.start(({ finished }) => {
      if (finished) {
        setCurrentFeature((prev) => (prev + 1) % features.length)
      }
    })

    return () => animation.stop()
  }, [currentFeature, features.length, progressAnim, restartSignal])

  useEffect(() => {
    imageAnim.setValue(0)
    Animated.timing(imageAnim, {
      toValue: 1,
      duration: 420,
      useNativeDriver: Platform.OS !== 'web',
    }).start()
  }, [currentFeature, imageAnim])

  useEffect(() => {
    if (!isStacked) return
    featureRailRef.current?.scrollTo({
      x: Math.max(currentFeature * featureRailScrollStep - 20, 0),
      animated: true,
    })
  }, [currentFeature, isStacked, featureRailScrollStep])

  const handleFeaturePress = useCallback((index: number) => {
    setCurrentFeature(index)
    setRestartSignal((signal) => signal + 1)
  }, [])

  const progressWidth = progressAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%'],
  })

  const imageTranslateY = imageAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [26, 0],
  })

  return (
    <View style={[styles.section, isPhone && styles.sectionPhone]} nativeID="features">
      <View style={styles.container}>
        <View style={[styles.sectionHead, isPhone && styles.sectionHeadPhone]}>
          <Text style={styles.eyebrow}>{t.featuresSection.eyebrow}</Text>
          <Text style={[styles.h2, isPhone && styles.h2Phone]}>{t.features.title}</Text>
          <Text style={[styles.p, isPhone && styles.pPhone]}>{t.features.subtitle}</Text>
        </View>

        <View style={[styles.showcase, isStacked && styles.showcaseStacked, isPhone && styles.showcasePhone]}>
          <ScrollView
            ref={featureRailRef}
            style={[styles.featureRail, isStacked && styles.featureRailStacked]}
            contentContainerStyle={
              isStacked ? styles.featureRailContentStacked : styles.featureRailContent
            }
            horizontal={isStacked}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
          >
            {features.map((feature, index) => {
              const isActive = currentFeature === index
              const Icon = feature.Icon

              return (
                <Pressable
                  key={feature.id}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isActive }}
                  accessibilityLabel={feature.title}
                  onPress={() => handleFeaturePress(index)}
                  style={({ hovered, pressed }) => [
                    styles.featureCard,
                    isStacked && styles.featureCardStacked,
                    phoneFeatureCardWidthStyle,
                    isActive && styles.featureCardActive,
                    hovered && Platform.OS === 'web' && styles.featureCardHovered,
                    pressed && styles.featureCardPressed,
                    Platform.OS === 'web' && WEB_HOVER_TRANSITION_STYLE,
                  ]}
                >
                  <View style={styles.featureCardTop}>
                    <View style={styles.featureHeadingLeft}>
                      <View style={[styles.featureIcon, isActive && styles.featureIconActive]}>
                        <Icon size={22} color={isActive ? theme.white : theme.primary} />
                      </View>
                      <Text style={[styles.featureTitle, isActive && styles.featureTitleActive]}>
                        {feature.title}
                      </Text>
                    </View>
                    <View style={styles.featureIndexPill}>
                      <Text style={styles.featureIndexText}>0{index + 1}</Text>
                    </View>
                  </View>
                  <Text style={[styles.featureBody, isActive && styles.featureBodyActive]}>
                    {feature.description}
                  </Text>

                  <View style={styles.progressTrack}>
                    {isActive && (
                      <Animated.View style={[styles.progressFill, { width: progressWidth }]} />
                    )}
                  </View>
                </Pressable>
              )
            })}
          </ScrollView>

          <View
            style={[
              styles.visualColumn,
              isStacked && styles.visualColumnStacked,
              isPhone && styles.visualColumnPhone,
            ]}
          >
            <LinearGradient
              colors={[theme.primary + '38', theme.accent + '29']}
              start={landingFeatureVisualGlowStart}
              end={landingFeatureVisualGlowEnd}
              style={styles.visualGlow}
            />
            <View style={[styles.visualFrame, isPhone && styles.visualFramePhone]}>
              <Animated.View
                key={activeFeature.id}
                style={[
                  styles.visualAnimated,
                  {
                    opacity: imageAnim,
                    transform: [{ translateY: imageTranslateY }],
                  },
                ]}
              >
                <Image
                  source={activeFeature.image}
                  style={[styles.visualImage, isPhone && styles.visualImagePhone]}
                  resizeMode="contain"
                  accessibilityIgnoresInvertColors
                  accessibilityLabel={activeFeature.accessibilityLabel}
                />
              </Animated.View>
            </View>

            <View style={[styles.visualCaption, isPhone && styles.visualCaptionPhone]}>
              <Text style={styles.visualCaptionKicker}>{t.featuresSection.livePreview}</Text>
              <Text style={styles.visualCaptionTitle}>{activeFeature.title}</Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  )
}
